import { Observable } from 'rxjs';
import { map, tap, filter } from 'rxjs/operators';

import { HttpClient, HttpEventType, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { FsDataDogRumService } from '@lib/data-dog-rum';
import { ITimelineDefinition } from '@lib/timeline';

import { ITimeline } from '../interfaces';

export interface TimelineContext {
  camera_id?: string;
  source?: 'cloud' | 'local';
}

@Injectable({ providedIn: 'root' })
export class TimelineService {
  public eventURL = 'https://14sw4h5s90.execute-api.us-east-1.amazonaws.com/dev/';

  constructor(
    private readonly _httpClient: HttpClient,
    private readonly _datadogService: FsDataDogRumService,
  ) {}

  public getTimelineData(
    url: string,
    context?: TimelineContext,
  ): Observable<ITimelineDefinition[]> {
    const startTime = performance.now();
    let ttfb_ms: number | undefined;

    return this._httpClient
      .get<ITimelineDefinition[]>(url, {
        observe: 'events',
        reportProgress: true,
      })
      .pipe(
        tap((event) => {
          if (event.type === HttpEventType.DownloadProgress && ttfb_ms === undefined) {
            ttfb_ms = Math.round(performance.now() - startTime);
          }
        }),
        filter((event) => event.type === HttpEventType.Response),
        map((event) => {
          const response = event as HttpResponse<ITimelineDefinition[]>;
          return {
            body: response.body || [],
            headers: response.headers,
          };
        }),
        tap(({ body, headers }) => {
          const endTime = performance.now();
          const total_ms = Math.round(endTime - startTime);
          const contentLength = headers.get('content-length');
          const size_bytes = contentLength
            ? parseInt(contentLength, 10)
            : body.length > 0
            ? JSON.stringify(body).length
            : 0;

          this._datadogService.addAction('timeline_loaded', {
            camera_id: context?.camera_id,
            source_ui: 'camera_timeline',
            source: context?.source,
            total_ms,
            size_bytes,
            ttfb_ms,
          });
        }),
        map(({ body }) => body),
      );
  }

  public export(url: string): Observable<any> {
    return this._httpClient.get(url, {
      responseType: 'blob',
      reportProgress: true,
      observe: 'events',
    });
  }

  public addToVault(
    cameraId: string,
    start: number,
    end: number,
    vaultIncidents?: any,
    notifyByText?: any,
    notifyByEmail?: any,
    saveAsFilename?: string,
  ): Observable<any> {
    return this._httpClient.post(`/api/cameras/${cameraId}/recordings/`, {
      start,
      end,
      vaultIncidents,
      notifyByText,
      notifyByEmail,
      saveAsFilename,
    });
  }

  public getAIData(id: string, params?: any): Observable<any> {
    return this._httpClient
      .get(`${this.eventURL}events/${id}`, { params })
      .pipe(map((response: any) => response.results));
  }

  public getCarColors(): Observable<any> {
    return this._httpClient
      .get(`${this.eventURL}settings/colors`)
      .pipe(map((response: any) => response.results));
  }

  public getTimelinePath(timeline: ITimeline[], startTime: number): any {
    const reversedTimeline = timeline.reverse();
    const formattedStartTime = startTime / 1000;
    let i = 0;
    while (i < reversedTimeline.length) {
      const start = reversedTimeline[i].start;
      const end = reversedTimeline[i].end;
      if (start < formattedStartTime && formattedStartTime < end) {
        return reversedTimeline[i];
      }
      i++;
    }
  }
}
